// ─────────────────────────────────────────────
//  Google Sheet Engine — the ON/OFF control, Setup → Facility → Google Sheet
//  Import tab.
//
//  The engine is an out-of-band process (its own service account) that syncs
//  the daily Google Sheet into the live board between the nightly imports.
//  This panel is the ONLY control surface it has: the app cannot start or stop
//  it, it publishes a flag the engine agrees to obey. The shape of that flag,
//  and what "ON" means, live in domain/sheetEngine.js — read that file first,
//  it is the contract the Python engine implements too.
//
//  ⚠️ ITS OWN FILE, NOT A SECTION INSIDE setupPage.sheetImport.js. That file is
//  already 562 lines, past the 500-line cap its own header cites — the same
//  reason SheetImportConfigPanel was split out of setupPage.facility.js.
//
//  ADMIN ONLY, and the Firestore rule says so too (facilities →
//  sheetEngineEnabled). The AIC's field allowlist on the facility doc covers
//  sheetImportConfig and breakWindows; this key is deliberately not in it. The
//  panel renders NOTHING for a non-admin rather than a disabled switch: a
//  dead control on a screen an AIC is otherwise allowed to edit reads as a
//  bug, and SetupPage admits AICs (same reasoning as MobileAppSection).
//
//  GLOBAL, NOT PER-CAMPUS. One sheet, one process, one switch — so it writes
//  facilities/{DEFAULT_FACILITY_ID}, the same doc globalSheetImportConfig sits
//  on, no matter which facility is selected in the tab above. The copy says so
//  out loud, because everything else on this panel IS per-facility.
// ─────────────────────────────────────────────
import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import { updateFacility } from "../firebase/firestore";
import { useFacility } from "../hooks/useFacility";
import {
  SHEET_ENGINE_ENABLED_FIELD,
  SHEET_ENGINE_STALE_AFTER_MS,
  SHEET_ENGINE_STATUS,
  sheetEngineState,
} from "../domain/sheetEngine";
import { DEFAULT_FACILITY_ID } from "../config/roles";
import tokens from "../theme/tokens";
import s from "./setupPage.styles";

const { color } = tokens;

// ⚠️ THE PANEL NEEDS ITS OWN CLOCK, and this is the whole reason the feature
// works. useFacility is a live snapshot listener, so a NEW heartbeat re-renders
// this component for free. But a STALLED engine writes nothing — there is no
// snapshot to react to — so without a tick the panel would freeze on the last
// good "ON — running / 12s ago" and never once say the engine had died. The
// state that matters most is the one no write announces.
//
// 15s: a quarter of the engine's promised check-in interval, so the age label
// never lags reality by more than a glance, and cheap enough on a wall device.
const TICK_MS = 15 * 1000;

// Tone → the visual treatment. Colour is NEVER the only carrier here (WCAG
// 1.4.1): every tone also renders its own words in `label`, and the dot has a
// text sibling. Inks are the audited pairs from tokens.js — accent.error is a
// FILL/BORDER value, it fails AA as text on both card and raised, so the error
// tone uses surface.error + errorInk instead of tinting the words red.
//
// The warn tone keeps border.default rather than tinting its edge amber:
// accent.closing (#f59e0b) on the raised ground is well under 1.4.11's 3:1, so
// an amber boundary would be a border that reads as decoration to anyone it
// was drawn for. border.default is measured at 3.21:1 on raised. The amber
// stays where it is legible — the dot, beside its own words.
const TONE_STYLE = {
  ok:      { dot: color.accent.active,  bg: color.surface.card,   ink: color.text.primary, border: color.border.default },
  warn:    { dot: color.accent.closing, bg: color.surface.raised, ink: color.text.primary, border: color.border.default },
  error:   { dot: color.accent.error,   bg: color.surface.error,  ink: color.surface.errorInk, border: color.accent.error },
  neutral: { dot: color.text.muted,     bg: color.surface.card,   ink: color.text.secondary, border: color.border.default },
};

const STALE_MINUTES = Math.round(SHEET_ENGINE_STALE_AFTER_MS / 60000);

export function SheetEngineSection() {
  const { isAdmin } = useAuth();
  const toast = useToast();
  const { facility: globalFacility } = useFacility(DEFAULT_FACILITY_ID);
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  // The tick. Runs regardless of state — an engine can stall at any moment, so
  // there is no state in which the panel is allowed to stop re-checking.
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => clearInterval(id);
  }, []);

  // Hooks run before this gate on purpose: a conditional return placed ABOVE a
  // hook fires the hook only when the condition flips, which is its own class
  // of bug in this codebase. Gate on the render, not on the hooks.
  if (!isAdmin) return null;

  // One reader for the switch AND the liveness, so the label and the toggle
  // can never disagree about what is happening.
  const state = sheetEngineState(globalFacility, { now });
  const tone = TONE_STYLE[state.tone] || TONE_STYLE.neutral;

  const flip = async () => {
    const next = !state.enabled;
    // Confirm the ARMING direction only. Switching ON hands an unattended
    // process permission to rewrite a live OR board from a spreadsheet;
    // switching OFF is the safe direction and must never be slowed down —
    // it is what someone reaches for when the sheet is wrong.
    if (next && !window.confirm(
      "Turn the Google Sheet Engine ON?\n\nThe engine will start applying changes from the daily Google Sheet to the live board automatically, without anyone pressing Apply."
    )) return;

    setBusy(true);
    try {
      await updateFacility(DEFAULT_FACILITY_ID, { [SHEET_ENGINE_ENABLED_FIELD]: next });
      // No local optimistic state: the switch renders from the live snapshot,
      // so what is shown is what is STORED. An optimistic flip that failed a
      // rule would leave the UI claiming ON while the engine read OFF — the
      // exact disagreement this whole module exists to prevent.
      toast.success(next
        ? "Google Sheet Engine switched ON. Watch for the engine's first check-in below."
        : "Google Sheet Engine switched OFF.");
    } catch (err) {
      console.error("Toggle Google Sheet Engine failed:", err);
      toast.error("Could not change the engine switch. Administrator rights are required.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${color.border.default}` }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: color.text.secondary, marginBottom: 6 }}>
        ⚙️ Google Sheet Engine (all facilities)
      </div>
      <p style={{ fontSize: 11, color: color.text.muted, margin: "0 0 10px" }}>
        Keeps the live board in step with the daily Google Sheet <strong>during the
        day</strong>, between the nightly imports. This switch is <strong>global</strong> —
        it is not per-campus, and it does not affect the midnight import, which runs
        either way.
      </p>

      <div style={{ ...s.notifRow, borderBottom: "none" }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600 }}>Google Sheet Engine</div>
          <div style={{ fontSize: 11, color: color.text.muted, marginTop: 3 }}>
            {state.enabled
              ? "The engine is authorised to write to the live board."
              : "The engine is not authorised to write to the live board."}
          </div>
        </div>
        <button
          style={{ ...s.toggle, ...(state.enabled ? s.toggleOn : {}) }}
          role="switch"
          aria-checked={state.enabled}
          aria-label="Google Sheet Engine"
          disabled={busy}
          onClick={flip}
        >
          <div style={{ ...s.toggleKnob, ...(state.enabled ? s.toggleKnobOn : {}) }} />
        </button>
      </div>

      {/* LIVENESS. The switch says what an admin asked for; this says what is
          actually happening. Without it, "ON" beside a dead engine is a lie the
          owner would act on — they would stop watching the sheet.

          role="status" wraps the LABEL ONLY, not the age. The label changes
          rarely (running → stalled) and is worth announcing; the age re-renders
          every 15 seconds and would turn a screen reader into a metronome. */}
      <div
        style={{
          display: "flex", alignItems: "flex-start", gap: 10,
          padding: "10px 12px", borderRadius: 8,
          background: tone.bg, border: `1px solid ${tone.border}`,
        }}
      >
        <span
          aria-hidden="true"
          style={{
            width: 10, height: 10, borderRadius: "50%", marginTop: 4,
            background: tone.dot, flexShrink: 0,
          }}
        />
        <div style={{ minWidth: 0 }}>
          <div role="status" style={{ fontSize: 13, fontWeight: 700, color: tone.ink }}>
            {state.label}
          </div>
          <div style={{ fontSize: 11, color: tone.ink, marginTop: 3 }}>
            {state.detail}
          </div>
          {state.message && (
            <div style={{ fontSize: 11, color: tone.ink, marginTop: 3, fontStyle: "italic" }}>
              Engine says: {state.message}
            </div>
          )}
          {/* The raw fact under the interpretation, so an operator can argue
              with the panel instead of just believing it. */}
          <div style={{ fontSize: 11, color: color.text.muted, marginTop: 3 }}>
            {state.ageLabel
              ? `Last engine check-in: ${state.ageLabel}.`
              : "The engine has never checked in."}
            {" "}Considered stopped after {STALE_MINUTES} minutes of silence.
          </div>
        </div>
      </div>

      {state.status === SHEET_ENGINE_STATUS.STALLED && (
        <p style={{ fontSize: 11, color: color.text.muted, margin: "8px 0 0" }}>
          Nothing on this screen can restart the engine — it runs outside the app.
          Switch it off if the board should not be waiting on it.
        </p>
      )}
    </div>
  );
}
